/* Admin Surveys — designer, activation, responses viewer + Excel export. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, Trash2, X, Download, PhoneCall, Pencil, Eye } from "lucide-react";

const QTYPES = [
  { v: "text", label: "Text answer" },
  { v: "yesno", label: "Yes / No" },
  { v: "rating", label: "Rating 1–5" },
  { v: "choice", label: "Multiple choice" },
];

export const downloadBlob = async (url, filename) => {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch { toast.error("Export failed"); }
};

export default function AdminSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [editing, setEditing] = useState(null); // survey object or "new"
  const [viewing, setViewing] = useState(null);
  const nav = useNavigate();

  const load = () => api.get("/surveys").then(({ data }) => setSurveys(data.surveys || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (s, status) => {
    try { await api.patch(`/surveys/${s.id}`, { status }); toast.success(`Survey ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and all its responses?`)) return;
    await api.delete(`/surveys/${s.id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-surveys-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ quality surveys</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><ClipboardList size={26} /> Surveys</h1>
        </div>
        <button onClick={() => setEditing("new")} data-testid="new-survey-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0C10] text-white text-sm font-bold rounded-md hover:bg-black">
          <Plus size={15} /> New survey
        </button>
      </div>

      {surveys.length === 0 ? (
        <div className="bg-white border border-dashed border-black/15 rounded-2xl p-12 text-center text-sm text-neutral-500">
          No surveys yet — create your first quality survey and hand it to your call team.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
              <tr>
                <th className="px-5 py-3">Survey</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Questions</th>
                <th className="px-5 py-3">Responses</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {surveys.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100 hover:bg-neutral-50/60" data-testid={`survey-row-${s.id}`}>
                  <td className="px-5 py-3 font-semibold">{s.title}</td>
                  <td className="px-5 py-3">
                    <select value={s.status} onChange={(e) => setStatus(s, e.target.value)} data-testid={`survey-status-${s.id}`}
                      className={`text-xs font-bold px-2 py-1 rounded-md border ${s.status === "active" ? "text-emerald-700 border-emerald-300 bg-emerald-50" : s.status === "closed" ? "text-red-600 border-red-200 bg-red-50" : "text-neutral-600 border-neutral-300"}`}>
                      <option value="draft">draft</option><option value="active">active</option><option value="closed">closed</option>
                    </select>
                  </td>
                  <td className="px-5 py-3 text-neutral-600">{s.questions?.length || 0}</td>
                  <td className="px-5 py-3 font-mono">{s.response_count || 0}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <button onClick={() => nav(`/surveys/run/${s.id}`)} title="Run this survey" data-testid={`survey-run-${s.id}`}
                        className="text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"><PhoneCall size={11} /> Run</button>
                      <button onClick={() => setViewing(s)} title="View responses" data-testid={`survey-view-${s.id}`}
                        className="text-xs px-3 py-1.5 rounded-md border border-neutral-300 hover:border-[#0A0C10] inline-flex items-center gap-1"><Eye size={11} /> Responses</button>
                      <button onClick={() => downloadBlob(`/surveys/${s.id}/export`, `${s.title}.xlsx`)} title="Export to Excel" data-testid={`survey-export-${s.id}`}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-neutral-300 hover:border-[#0A0C10]"><Download size={12} /></button>
                      <button onClick={() => setEditing(s)} title="Edit" data-testid={`survey-edit-${s.id}`}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-neutral-300 hover:border-[#0A0C10]"><Pencil size={12} /></button>
                      <button onClick={() => del(s)} title="Delete" data-testid={`survey-delete-${s.id}`}
                        className="text-xs px-2.5 py-1.5 rounded-md border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <SurveyDesigner survey={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {viewing && <ResponsesModal survey={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function SurveyDesigner({ survey, onClose, onSaved }) {
  const [title, setTitle] = useState(survey?.title || "");
  const [description, setDescription] = useState(survey?.description || "");
  const [questions, setQuestions] = useState(survey?.questions?.length ? survey.questions : [{ text: "", type: "text", options: [] }]);
  const [saving, setSaving] = useState(false);

  const upd = (i, patch) => setQuestions((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)));
  const save = async () => {
    setSaving(true);
    try {
      const body = { title, description, questions };
      if (survey) await api.patch(`/surveys/${survey.id}`, body);
      else await api.post("/surveys", { ...body, status: "active" });
      toast.success(survey ? "Survey updated" : "Survey created & activated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-designer">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-xl">{survey ? "Edit survey" : "New survey"}</h2>
          <button onClick={onClose} data-testid="survey-designer-close" className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={16} /></button>
        </div>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Survey title" data-testid="survey-title-input"
          className="w-full border border-neutral-300 rounded-md px-3 py-2.5 font-semibold outline-none focus:border-[#0A0C10]" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Instructions for callers (optional)" data-testid="survey-desc-input"
          className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]" />
        <div className="space-y-3">
          {questions.map((q, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid={`designer-q-${i}`}>
              <div className="flex gap-2">
                <input value={q.text} onChange={(e) => upd(i, { text: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`designer-q-text-${i}`}
                  className="flex-1 border border-neutral-300 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0A0C10]" />
                <select value={q.type} onChange={(e) => upd(i, { type: e.target.value })} data-testid={`designer-q-type-${i}`}
                  className="border border-neutral-300 rounded-md px-2 py-2 text-xs bg-white">
                  {QTYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
                <button onClick={() => setQuestions((qs) => qs.filter((_, j) => j !== i))} data-testid={`designer-q-del-${i}`}
                  className="px-2 rounded-md border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
              {q.type === "choice" && (
                <input value={(q.options || []).join(", ")} onChange={(e) => upd(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                  placeholder="Options, comma separated (e.g. Excellent, Good, Poor)" data-testid={`designer-q-options-${i}`}
                  className="w-full border border-dashed border-neutral-300 rounded-md px-3 py-1.5 text-xs outline-none focus:border-[#0A0C10]" />
              )}
            </div>
          ))}
        </div>
        <button onClick={() => setQuestions((qs) => [...qs, { text: "", type: "text", options: [] }])} data-testid="designer-add-q"
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-neutral-300 hover:border-[#0A0C10]">
          <Plus size={13} /> Add question
        </button>
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-neutral-300">Cancel</button>
          <button onClick={save} disabled={saving || !title.trim()} data-testid="survey-save-btn"
            className="px-6 py-2 text-sm font-bold rounded-md bg-[#0A0C10] text-white hover:bg-black disabled:opacity-50">
            {saving ? "Saving…" : survey ? "Save changes" : "Create & activate"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResponsesModal({ survey, onClose }) {
  const [responses, setResponses] = useState(null);
  useEffect(() => {
    api.get(`/surveys/${survey.id}/responses`).then(({ data }) => setResponses(data.responses || [])).catch(() => setResponses([]));
  }, [survey.id]);
  const qmap = Object.fromEntries((survey.questions || []).map((q) => [q.id, q.text]));
  return (
    <div className="fixed inset-0 z-[90] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="responses-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display font-bold text-xl">{survey.title}</h2>
            <div className="text-xs text-neutral-500">{responses?.length ?? "…"} responses</div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => downloadBlob(`/surveys/${survey.id}/export`, `${survey.title}.xlsx`)} data-testid="responses-export-btn"
              className="text-xs font-semibold px-3 py-2 rounded-md border border-neutral-300 hover:border-[#0A0C10] inline-flex items-center gap-1.5"><Download size={12} /> Excel</button>
            <button onClick={onClose} data-testid="responses-close" className="p-2 hover:bg-neutral-100 rounded-md"><X size={16} /></button>
          </div>
        </div>
        {responses === null ? <div className="text-sm text-neutral-500">Loading…</div> :
          responses.length === 0 ? <div className="text-sm text-neutral-500 py-8 text-center">No responses yet.</div> : (
            <div className="space-y-4">
              {responses.map((r) => (
                <div key={r.id} className="border border-neutral-200 rounded-lg p-4" data-testid={`response-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <span className="font-bold">{r.teacher_name}</span>
                    <span className="text-xs text-neutral-500 font-mono">{r.teacher_phone}</span>
                    <span className="ml-auto text-xs text-neutral-400">by {r.caller_name} · {(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {(r.answers || []).map((a) => (
                      <div key={a.question_id} className="text-xs">
                        <span className="text-neutral-500">{qmap[a.question_id] || "?"}:</span>{" "}
                        <span className="font-semibold">{a.value || "—"}</span>
                        {a.note && <span className="text-neutral-500 italic"> · note: {a.note}</span>}
                      </div>
                    ))}
                    {r.overall_note && <div className="text-xs text-neutral-600 border-t border-neutral-100 pt-1.5 mt-1.5"><strong>Call notes:</strong> {r.overall_note}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
